import { c as _c } from "react/compiler-runtime";
import { Card, CardContent, Divider, Stack, styled, Switch, Tooltip, Typography, type Theme } from '@mui/material';
import type { Condition, BackupPolicyConfigResponse } from '@repo/api-gw-sdk';
import { BackupPolicyType, BackupStatus, ConditionOperatorEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { createBackupStatusNotInCondition } from '@/components/backupStatus/createBackupStatusNotInCondition';
import { ButtonWithIcon } from '@/components/buttons/buttonWithIcon';
import { ConfirmationDialog } from '@/components/dialogs/confirmationDialog';
import { OptionMenu } from '@/components/option-menu/option-menu';
import { backupPolicies } from '@/components/queryBuilder/properties';
import { getResourcesScopeType, type ResourcesScopeType } from '@/components/resourcesSelection/resourceSelectionDynamicViewer';
import { ResourcesSelectionViewer } from '@/components/resourcesSelection/resourcesSelectionViewer';
import { NavigationLink } from '@/components/shared/link';
import { Loader } from '@/components/shared/loader';
import { OverflowTooltip } from '@/components/tooltips/overflowTooltip';
import { AccountAsVaultTag, VaultTag } from '@/components/vaults/vaultTag';
import { useRoles } from '@/contexts/useRoles';
import { useWorkspace } from '@/contexts/useWorkspace';
import type { BackupVault } from '@/data/vaults/backupVault';
import { createCombineCondition } from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';
import { BackupPolicyConditionTitle } from './BackupPolicyConditionEditor';
import { EntraIdResourceSelectionViewer } from './EntraIdResourceSelectionViewer';
import { GwsResourceSelectionViewer } from './GwsResourceSelectionViewer';
import { M365ResourceSelectionViewer } from './M365ResourceSelectionViewer';
import { backupPolicyNativeProvider, isAzureEntraIdPolicyType, isContinuousPolicyType, isGoogleWorkspacePolicyType, isMicrosoft365PolicyType, isNativePolicyType, isPitrPolicyType, isSingleTenantSaasPolicyType } from './backupPoliciesTypes';
import { useAllBackupPolicyProperties } from './backupPolicyProperties';
import { BackupPolicyContinuousSchedulesTable, BackupPolicySchedulesTable } from './backupPolicySchedulesTable';
import { BackupPolicyTypeTag } from './backupPolicyTypeTag';
import { BackupPolicyTypeDescription } from './customPolicySupportedDisclaimer';
export const BPCard = styled(Card)((props: {
  disabled: boolean;
  theme?: Theme;
  style?: object;
}) => {
  return {
    border: '1px solid var(--mui-palette-divider)',
    backgroundColor: props.disabled ? 'var(--mui-palette-background-default)' : 'var(--mui-palette-background-paper)',
    ...props.theme?.typography.body2,
    textAlign: 'left',
    color: props.theme?.palette.text.secondary,
    minWidth: '550px',
    minHeight: '170px',
    boxShadow: 'none',
    paddingTop: '12px',
    borderRadius: '16px',
    '& .MuiCardContent-root': {
      padding: '0px 24px 24px'
    },
    ...props.style
  };
});
interface BackupPolicyCardProps {
  processing?: boolean;
  policy: BackupPolicyConfigResponse;
  vaults?: BackupVault[];
  defaultExpanded?: boolean;
  onEdit?: (policy: BackupPolicyConfigResponse) => void;
  onDelete?: (policy: BackupPolicyConfigResponse) => void;
  onDuplicate?: (policy: BackupPolicyConfigResponse) => void;
  onEnabledPolicyUpdate?: (policy: BackupPolicyConfigResponse) => Promise<boolean>;
}
export function BackupPolicyCard(props) {
  const $ = _c(171);
  const enabled = props.policy.enabled;
  const {
    snackbar
  } = useWorkspace();
  const {
    isAuthorized
  } = useRoles();
  const allBackupPolicyProperties = useAllBackupPolicyProperties();
  const [vaultsSectionExpanded, setVaultsSectionExpanded] = useState(!!props.defaultExpanded);
  const [disablePolicyDialogOpen, setDisablePolicyDialogOpen] = useState(false);
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let t0;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let togglePolicyEnabledState;
  if ($[0] !== allBackupPolicyProperties || $[1] !== enabled || $[2] !== isAuthorized || $[3] !== props || $[4] !== snackbar || $[5] !== vaultsSectionExpanded) {
    const matchingVaults = props.vaults?.filter(vault => props.policy?.schedules.find(schedule => schedule.vaultId === vault.id)).slice(0, 2);
    let t15;
    if ($[28] !== props.policy.condition) {
      t15 = getResourcesScopeType(props.policy.condition, "allIncluded");
      $[28] = props.policy.condition;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    const resourcesScopeType = t15;
    let t16;
    if ($[30] !== props.policy.policyType) {
      t16 = isSingleTenantSaasPolicyType(props.policy.policyType);
      $[30] = props.policy.policyType;
      $[31] = t16;
    } else {
      t16 = $[31];
    }
    const isSaasWorkspace = t16;
    let t17;
    if ($[32] !== props || $[33] !== snackbar) {
      t17 = async () => {
        const enabled_0 = props.policy.enabled;
        const updated = await props.onEnabledPolicyUpdate?.(props.policy);
        if (updated === false) {
          return;
        }
        snackbar.showMessage(<><span className="font-semibold">{props.policy.name}</span> backup policy is now {enabled_0 ? "disabled" : "enabled"}.</>);
      };
      $[32] = props;
      $[33] = snackbar;
      $[34] = t17;
    } else {
      t17 = $[34];
    }
    togglePolicyEnabledState = t17;
    let t18;
    if ($[35] !== enabled || $[36] !== togglePolicyEnabledState) {
      t18 = () => {
        if (enabled) {
          setDisablePolicyDialogOpen(true);
        } else {
          togglePolicyEnabledState();
        }
      };
      $[35] = enabled;
      $[36] = togglePolicyEnabledState;
      $[37] = t18;
    } else {
      t18 = $[37];
    }
    const onToggleClicked = t18;
    const t19 = props.policy?.id || "";
    let T6;
    let T7;
    let t20;
    let t21;
    let t22;
    let t23;
    let t24;
    let t25;
    if ($[38] !== enabled || $[39] !== isAuthorized || $[40] !== isSaasWorkspace || $[41] !== props.onDelete || $[42] !== props.onDuplicate || $[43] !== props.onEdit || $[44] !== props.onEnabledPolicyUpdate || $[45] !== props.policy.attachedResources || $[46] !== props.policy.name || $[47] !== props.policy.policyType || $[48] !== props.processing || $[49] !== t19) {
      const inventoryFilterCondition = {
        operator: ConditionOperatorEnum.ContainsAnyOf,
        value: [t19],
        property: backupPolicies.name
      };
      T5 = BPCard;
      t13 = !enabled;
      t14 = "backup-policy-card";
      T7 = Stack;
      t23 = "row";
      if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
        t24 = {
          maxWidth: "100%",
          justifyContent: "space-between",
          gap: "12px",
          alignItems: "center",
          margin: "12px 24px"
        };
        $[61] = t24;
      } else {
        t24 = $[61];
      }
      let t26;
      if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
        t26 = {
          alignItems: "center",
          gap: "12px",
          minWidth: "0px",
          flexShrink: 1
        };
        $[62] = t26;
      } else {
        t26 = $[62];
      }
      let t27;
      if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
        t27 = {
          minWidth: "0px",
          flexShrink: 1
        };
        $[63] = t27;
      } else {
        t27 = $[63];
      }
      let t28;
      if ($[64] !== props.policy.name) {
        t28 = <Stack sx={t27}><OverflowTooltip text={props.policy.name} renderContent={_temp} /></Stack>;
        $[64] = props.policy.name;
        $[65] = t28;
      } else {
        t28 = $[65];
      }
      let t29;
      if ($[66] !== props.policy.policyType) {
        t29 = <BackupPolicyTypeTag policyType={props.policy.policyType} />;
        $[66] = props.policy.policyType;
        $[67] = t29;
      } else {
        t29 = $[67];
      }
      if ($[68] !== t28 || $[69] !== t29) {
        t25 = <Stack direction="row" sx={t26}>{t28}{t29}</Stack>;
        $[68] = t28;
        $[69] = t29;
        $[70] = t25;
      } else {
        t25 = $[70];
      }
      T6 = Stack;
      t20 = "row";
      if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
        t21 = {
          alignItems: "center",
          gap: "16px"
        };
        $[71] = t21;
      } else {
        t21 = $[71];
      }
      t22 = enabled && !isSaasWorkspace && <>{props.processing ? <Loader size="medium" /> : <NavigationLink path="/inventory" searchParams={{
          query: createCombineCondition([inventoryFilterCondition, createBackupStatusNotInCondition([BackupStatus.ExcludedFromBackup, BackupStatus.Disconnected, BackupStatus.Unsupported, BackupStatus.LimitExceeded])])
        }} disabled={!isAuthorized("read:inventory")} className="text-(--mui-palette-primary-main)"><ButtonWithIcon text={`${props.policy.attachedResources ?? 0} ${pluralize("Resource", props.policy.attachedResources ?? 0)}`} iconPosition="right" icon="material-symbols-arrow-right-alt" /></NavigationLink>}{(props.onEdit && props.onDelete && props.onDuplicate || props.onEnabledPolicyUpdate) && <Divider sx={{
          height: "16px"
        }} orientation="vertical" />}</>;
      $[38] = enabled;
      $[39] = isAuthorized;
      $[40] = isSaasWorkspace;
      $[41] = props.onDelete;
      $[42] = props.onDuplicate;
      $[43] = props.onEdit;
      $[44] = props.onEnabledPolicyUpdate;
      $[45] = props.policy.attachedResources;
      $[46] = props.policy.name;
      $[47] = props.policy.policyType;
      $[48] = props.processing;
      $[49] = t19;
      $[50] = T5;
      $[51] = T6;
      $[52] = T7;
      $[53] = t13;
      $[54] = t14;
      $[55] = t20;
      $[56] = t21;
      $[57] = t22;
      $[58] = t23;
      $[59] = t24;
      $[60] = t25;
    } else {
      T5 = $[50];
      T6 = $[51];
      T7 = $[52];
      t13 = $[53];
      t14 = $[54];
      t20 = $[55];
      t21 = $[56];
      t22 = $[57];
      t23 = $[58];
      t24 = $[59];
      t25 = $[60];
    }
    let t26;
    if ($[72] !== isAuthorized || $[73] !== isSaasWorkspace || $[74] !== props) {
      t26 = (isSaasWorkspace && props.onDelete || props.onEdit && props.onDelete && props.onDuplicate) && <OptionMenu options={[...(isSaasWorkspace ? [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }] : [{
        id: "edit-policy",
        text: "Edit",
        menuItemProps: {
          disabled: !isAuthorized("update:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onEdit?.(props.policy);
          }
        }
      }, {
        id: "duplicate-policy",
        text: "Duplicate",
        menuItemProps: {
          disabled: !isAuthorized("create:policies"),
          onClick: () => {
            props.onDuplicate?.(props.policy);
          }
        }
      }, {
        id: "divider",
        divider: true
      }]), {
        id: "delete-policy",
        text: "Delete",
        menuItemProps: {
          disabled: !isAuthorized("delete:policies"),
          onClick: () => {
            if (!props.policy) {
              return;
            }
            props.onDelete?.(props.policy);
          }
        }
      }]} />;
      $[72] = isAuthorized;
      $[73] = isSaasWorkspace;
      $[74] = props;
      $[75] = t26;
    } else {
      t26 = $[75];
    }
    let t27;
    if ($[76] !== enabled || $[77] !== isAuthorized || $[78] !== onToggleClicked || $[79] !== props.onEnabledPolicyUpdate) {
      t27 = props.onEnabledPolicyUpdate && <Tooltip title={enabled ? "This policy is enabled" : "This policy is disabled"}><Switch disabled={!isAuthorized("update:policies")} checked={enabled} onChange={onToggleClicked} /></Tooltip>;
      $[76] = enabled;
      $[77] = isAuthorized;
      $[78] = onToggleClicked;
      $[79] = props.onEnabledPolicyUpdate;
      $[80] = t27;
    } else {
      t27 = $[80];
    }
    let t28;
    if ($[81] !== T6 || $[82] !== t20 || $[83] !== t21 || $[84] !== t22 || $[85] !== t26 || $[86] !== t27) {
      t28 = <T6 direction={t20} sx={t21}>{t22}{t26}{t27}</T6>;
      $[81] = T6;
      $[82] = t20;
      $[83] = t21;
      $[84] = t22;
      $[85] = t26;
      $[86] = t27;
      $[87] = t28;
    } else {
      t28 = $[87];
    }
    if ($[88] !== T7 || $[89] !== t23 || $[90] !== t24 || $[91] !== t25 || $[92] !== t28) {
      t12 = <T7 direction={t23} sx={t24}>{t25}{t28}</T7>;
      $[88] = T7;
      $[89] = t23;
      $[90] = t24;
      $[91] = t25;
      $[92] = t28;
      $[93] = t12;
    } else {
      t12 = $[93];
    }
    T4 = CardContent;
    if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        marginTop: "24px",
        "&&": {
          padding: "0 0 24px"
        }
      };
      $[94] = t11;
    } else {
      t11 = $[94];
    }
    T3 = Stack;
    if ($[95] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <Divider />;
      t9 = {
        gap: "12px"
      };
      $[95] = t8;
      $[96] = t9;
    } else {
      t8 = $[95];
      t9 = $[96];
    }
    let t29;
    if ($[97] === Symbol.for("react.memo_cache_sentinel")) {
      t29 = {
        px: "24px"
      };
      $[97] = t29;
    } else {
      t29 = $[97];
    }
    let t30;
    if ($[98] === Symbol.for("react.memo_cache_sentinel")) {
      t30 = <AccordionSummary><Typography variant="h4">{"Resource selection"}</Typography></AccordionSummary>;
      $[98] = t30;
    } else {
      t30 = $[98];
    }
    let t31;
    if ($[99] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = {
        gap: "24px"
      };
      $[99] = t31;
    } else {
      t31 = $[99];
    }
    let t32;
    if ($[100] !== allBackupPolicyProperties || $[101] !== props.policy.condition || $[102] !== props.policy.exclude || $[103] !== props.policy.include || $[104] !== props.policy.policyType || $[105] !== props.policy.resourceTypes || $[106] !== resourcesScopeType) {
      t32 = isGoogleWorkspacePolicyType(props.policy.policyType) ? <GwsResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isMicrosoft365PolicyType(props.policy.policyType) ? <M365ResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : isAzureEntraIdPolicyType(props.policy.policyType) ? <EntraIdResourceSelectionViewer resourceId={props.policy.include?.[0]} /> : <ResourcesSelectionViewer condition={props.policy.condition} conditionTitle={<BackupPolicyConditionTitle resourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType ?? BackupPolicyType.BACKUP_POLICY_TYPE_STANDARD} />} include={props.policy.include ?? []} exclude={props.policy.exclude ?? []} resourcesScopeType={resourcesScopeType} dynamicResourcesHeader={null} allExcludedDescription={"This backup policy won\u2019t be automatically applied to any resources. Only manually included resources will be assigned to the policy."} allIncludedDescription={isPitrPolicyType(props.policy.policyType) ? "This backup policy will be automatically applied to all resources of the selected type. When new ones are discovered, they\u2019ll be included too." : "This backup policy will be automatically applied to all resources. When new ones are discovered, they\u2019ll be included too."} supportProperties={allBackupPolicyProperties.flat()} selectedResourceTypes={props.policy.resourceTypes} policyType={props.policy.policyType} />;
      $[100] = allBackupPolicyProperties;
      $[101] = props.policy.condition;
      $[102] = props.policy.exclude;
      $[103] = props.policy.include;
      $[104] = props.policy.policyType;
      $[105] = props.policy.resourceTypes;
      $[106] = resourcesScopeType;
      $[107] = t32;
    } else {
      t32 = $[107];
    }
    let t33;
    if ($[108] !== props.policy.policyType || $[109] !== props.policy.vaultStorageTier) {
      t33 = <BackupPolicyTypeDescription policyType={props.policy.policyType} showForPitr={false} vaultStorageTier={props.policy.vaultStorageTier} />;
      $[108] = props.policy.policyType;
      $[109] = props.policy.vaultStorageTier;
      $[110] = t33;
    } else {
      t33 = $[110];
    }
    let t34;
    if ($[111] !== t32 || $[112] !== t33) {
      t34 = <AccordionDetails><Stack sx={t31}>{t32}{t33}</Stack></AccordionDetails>;
      $[111] = t32;
      $[112] = t33;
      $[113] = t34;
    } else {
      t34 = $[113];
    }
    if ($[114] !== props.defaultExpanded || $[115] !== t34) {
      t10 = <Accordion defaultExpanded={props.defaultExpanded} sx={t29}>{t30}{t34}</Accordion>;
      $[114] = props.defaultExpanded;
      $[115] = t34;
      $[116] = t10;
    } else {
      t10 = $[116];
    }
    T2 = Accordion;
    t5 = vaultsSectionExpanded;
    if ($[117] !== vaultsSectionExpanded) {
      t6 = () => setVaultsSectionExpanded(!vaultsSectionExpanded);
      $[117] = vaultsSectionExpanded;
      $[118] = t6;
    } else {
      t6 = $[118];
    }
    if ($[119] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = {
        px: "24px"
      };
      $[119] = t7;
    } else {
      t7 = $[119];
    }
    T1 = AccordionSummary;
    T0 = Stack;
    t0 = "row";
    t1 = "w-full";
    if ($[120] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = {
        justifyContent: "space-between",
        alignItems: "center"
      };
      t3 = <Typography variant="h4">Backup schedules</Typography>;
      $[120] = t2;
      $[121] = t3;
    } else {
      t2 = $[120];
      t3 = $[121];
    }
    t4 = !vaultsSectionExpanded && <div>{isNativePolicyType(props.policy.policyType) ? <AccountAsVaultTag cloudProvider={backupPolicyNativeProvider(props.policy)} region={props.policy.schedules?.[0]?.region || "Source region"} /> : !!matchingVaults?.length && matchingVaults.map(_temp2)}</div>;
    $[0] = allBackupPolicyProperties;
    $[1] = enabled;
    $[2] = isAuthorized;
    $[3] = props;
    $[4] = snackbar;
    $[5] = vaultsSectionExpanded;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = T3;
    $[10] = T4;
    $[11] = T5;
    $[12] = t0;
    $[13] = t1;
    $[14] = t10;
    $[15] = t11;
    $[16] = t12;
    $[17] = t13;
    $[18] = t14;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
    $[27] = togglePolicyEnabledState;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    T3 = $[9];
    T4 = $[10];
    T5 = $[11];
    t0 = $[12];
    t1 = $[13];
    t10 = $[14];
    t11 = $[15];
    t12 = $[16];
    t13 = $[17];
    t14 = $[18];
    t2 = $[19];
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
    togglePolicyEnabledState = $[27];
  }
  let t15;
  if ($[122] !== T0 || $[123] !== t0 || $[124] !== t1 || $[125] !== t2 || $[126] !== t3 || $[127] !== t4) {
    t15 = <T0 direction={t0} className={t1} sx={t2}>{t3}{t4}</T0>;
    $[122] = T0;
    $[123] = t0;
    $[124] = t1;
    $[125] = t2;
    $[126] = t3;
    $[127] = t4;
    $[128] = t15;
  } else {
    t15 = $[128];
  }
  let t16;
  if ($[129] !== T1 || $[130] !== t15) {
    t16 = <T1>{t15}</T1>;
    $[129] = T1;
    $[130] = t15;
    $[131] = t16;
  } else {
    t16 = $[131];
  }
  let t17;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      paddingRight: 0,
      paddingTop: "24px"
    };
    $[132] = t17;
  } else {
    t17 = $[132];
  }
  let t18;
  if ($[133] !== props.policy || $[134] !== props.vaults) {
    t18 = <AccordionDetails sx={t17}>{isContinuousPolicyType(props.policy.policyType) ? <BackupPolicyContinuousSchedulesTable policy={props.policy} /> : <BackupPolicySchedulesTable policy={props.policy} vaults={props.vaults} />}</AccordionDetails>;
    $[133] = props.policy;
    $[134] = props.vaults;
    $[135] = t18;
  } else {
    t18 = $[135];
  }
  let t19;
  if ($[136] !== T2 || $[137] !== t16 || $[138] !== t18 || $[139] !== t5 || $[140] !== t6 || $[141] !== t7) {
    t19 = <T2 expanded={t5} onChange={t6} sx={t7}>{t16}{t18}</T2>;
    $[136] = T2;
    $[137] = t16;
    $[138] = t18;
    $[139] = t5;
    $[140] = t6;
    $[141] = t7;
    $[142] = t19;
  } else {
    t19 = $[142];
  }
  let t20;
  if ($[143] !== T3 || $[144] !== t10 || $[145] !== t19 || $[146] !== t8 || $[147] !== t9) {
    t20 = <T3 divider={t8} sx={t9}>{t10}{t19}</T3>;
    $[143] = T3;
    $[144] = t10;
    $[145] = t19;
    $[146] = t8;
    $[147] = t9;
    $[148] = t20;
  } else {
    t20 = $[148];
  }
  let t21;
  if ($[149] !== T4 || $[150] !== t11 || $[151] !== t20) {
    t21 = <T4 sx={t11}>{t20}</T4>;
    $[149] = T4;
    $[150] = t11;
    $[151] = t20;
    $[152] = t21;
  } else {
    t21 = $[152];
  }
  let t22;
  if ($[153] !== props.policy.name) {
    t22 = <>The <span className="font-semibold">{props.policy.name}</span>{" "}backup policy will stop being applied to resources. Existing snapshots will be kept until their retention expires.</>;
    $[153] = props.policy.name;
    $[154] = t22;
  } else {
    t22 = $[154];
  }
  let t23;
  if ($[155] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = () => setDisablePolicyDialogOpen(false);
    $[155] = t23;
  } else {
    t23 = $[155];
  }
  let t24;
  if ($[156] !== togglePolicyEnabledState) {
    t24 = async () => {
      await togglePolicyEnabledState();
      setDisablePolicyDialogOpen(false);
    };
    $[156] = togglePolicyEnabledState;
    $[157] = t24;
  } else {
    t24 = $[157];
  }
  let t25;
  if ($[158] !== disablePolicyDialogOpen || $[159] !== t22 || $[160] !== t24) {
    t25 = <ConfirmationDialog isOpen={disablePolicyDialogOpen} title="Disable backup policy" description={t22} onCancel={t23} onConfirm={t24} buttonTitle="Disable Policy" />;
    $[158] = disablePolicyDialogOpen;
    $[159] = t22;
    $[160] = t24;
    $[161] = t25;
  } else {
    t25 = $[161];
  }
  let t26;
  if ($[162] !== t12 || $[163] !== t21 || $[164] !== t25) {
    t26 = <>{t12}{t21}{t25}</>;
    $[162] = t12;
    $[163] = t21;
    $[164] = t25;
    $[165] = t26;
  } else {
    t26 = $[165];
  }
  let t27;
  if ($[166] !== T5 || $[167] !== t13 || $[168] !== t14 || $[169] !== t26) {
    t27 = <T5 disabled={t13} data-testid={t14}>{t26}</T5>;
    $[166] = T5;
    $[167] = t13;
    $[168] = t14;
    $[169] = t26;
    $[170] = t27;
  } else {
    t27 = $[170];
  }
  return t27;
}
function _temp2(vault_0) {
  return <VaultTag key={vault_0.id} vault={vault_0} />;
}
function _temp(text, ref) {
  return <Typography noWrap={true} variant="h3" ref={ref}>{text}</Typography>;
}